import { createTheme, type Theme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';
import { FONT_FAMILY, LAYOUT, PALETTE_DARK, PALETTE_LIGHT } from './tokens';

export type ColorMode = 'light' | 'dark';

/**
 * Builds the one platform theme.
 *
 * Material 2 "Classic" is MUI's own default look — elevation shadows, uppercase
 * buttons, 4px controls, blue primary — so most of this file is deliberately thin:
 * we accept MUI's defaults and only pin the decisions the platform actually cares
 * about. Switching the platform to Material 3 or a dense variant later is a change
 * to this file plus tokens.ts, and to nothing else.
 */
export function createPlatformTheme(mode: ColorMode = 'light'): Theme {
  const palette = mode === 'dark' ? PALETTE_DARK : PALETTE_LIGHT;

  const theme = createTheme({
    palette: { mode, ...palette },

    spacing: LAYOUT.spacingUnit,

    shape: { borderRadius: LAYOUT.controlRadius },

    typography: {
      fontFamily: FONT_FAMILY,
      // Page titles. Material 2's h4 scale is heavier than an enterprise screen
      // wants, so the display sizes are pulled in a little.
      h4: { fontSize: '1.5rem', fontWeight: 500, letterSpacing: '0em' },
      h5: { fontSize: '1.25rem', fontWeight: 500 },
      h6: { fontSize: '1rem', fontWeight: 500 },
      // Section labels above cards and KPI values.
      overline: {
        fontSize: '0.6875rem',
        fontWeight: 500,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        lineHeight: 1.6,
      },
      /**
       * Buttons read as written.
       *
       * Material's default uppercases every label and widens the tracking, which
       * turns "Create from File" into "CREATE FROM FILE" and makes a toolbar of six
       * buttons shout. The source applications set their labels in sentence case and
       * the platform follows them: 0.875rem / 500, no transform.
       */
      button: {
        fontSize: '0.875rem',
        fontWeight: 500,
        letterSpacing: 0,
        textTransform: 'none',
      },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { WebkitFontSmoothing: 'antialiased' },
        },
      },

      // The app bar reads as a surface, not as a coloured banner — the artifact's
      // white bar with an elevation-4 shadow.
      MuiAppBar: {
        defaultProps: { elevation: 4, color: 'inherit' },
        styleOverrides: {
          root: ({ theme: t }) => ({
            backgroundColor: t.palette.background.paper,
            color: t.palette.text.primary,
            backgroundImage: 'none',
          }),
        },
      },

      MuiCard: {
        defaultProps: { elevation: 1 },
        styleOverrides: {
          root: { borderRadius: LAYOUT.cardRadius, backgroundImage: 'none' },
        },
      },

      MuiPaper: {
        styleOverrides: { root: { backgroundImage: 'none' } },
      },

      MuiCardHeader: {
        defaultProps: { titleTypographyProps: { variant: 'h6' } },
      },

      // Nav items are pill-shaped rows inside the drawer.
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            borderRadius: LAYOUT.cardRadius,
            marginInline: t.spacing(1),
            paddingBlock: t.spacing(1.25),
            '&.Mui-selected': {
              backgroundColor: t.palette.action.selected,
              color: t.palette.primary.main,
              '& .MuiListItemIcon-root': { color: t.palette.primary.main },
              '&:hover': { backgroundColor: t.palette.action.selected },
            },
          }),
        },
      },

      MuiListItemIcon: {
        styleOverrides: { root: { minWidth: 40 } },
      },

      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' },
      },

      MuiTooltip: {
        defaultProps: { arrow: true },
      },

      MuiTableCell: {
        styleOverrides: {
          head: ({ theme: t }) => ({
            fontWeight: 600,
            fontSize: '0.75rem',
            letterSpacing: '0.02em',
            color: t.palette.text.secondary,
          }),
        },
      },

      // MUI X DataGrid (Community/MIT) is the platform's one table component.
      MuiDataGrid: {
        defaultProps: {
          density: 'standard',
          disableRowSelectionOnClick: true,
          rowHeight: 48,
        },
        styleOverrides: {
          root: ({ theme: t }) => ({
            border: 'none',
            '--DataGrid-rowBorderColor': t.palette.divider,
            '& .MuiDataGrid-columnHeaders': { backgroundColor: t.palette.background.paper },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              fontSize: '0.75rem',
              letterSpacing: '0.02em',
              color: t.palette.text.secondary,
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: `2px solid ${t.palette.primary.main}`,
              outlineOffset: -2,
            },
            '& .MuiDataGrid-footerContainer': { borderTop: `1px solid ${t.palette.divider}` },
          }),
        },
      },
    },
  });

  return theme;
}
